import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { SUPABASE_FN_BASE, TK_FLAGS } from "../config/endpoints";
import { fetchTkProducts, fetchTkBundles } from "./tkCatalog";

// Phase 5 (VITE_TK_CATALOG=1): product/bundle DISPLAY reads come from treatink-api,
// adapted to the legacy shape inside tkCatalog.js — consumers unchanged. Signed image
// URLs in those responses are short-lived, hence the low keepUnusedDataFor.
// Stripe pricing stays on Supabase either way (rule L4).
const tkQuery = (fetcher) => ({
  queryFn: async () => {
    try {
      return { data: await fetcher() };
    } catch (error) {
      return { error: { status: "CUSTOM_ERROR", error: String(error) } };
    }
  },
  keepUnusedDataFor: 240,
});

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: SUPABASE_FN_BASE,
  }),
  endpoints: (builder) => ({
    getAffiliateByRef: builder.query({
      query: (ref) => `/affiliates/${ref}`,
    }),
    verifyEditableItem: builder.query({
      query: (itemId) => `/verify-label-edit/${itemId}`,
    }),
    getAffiliateProductsMeta: builder.query({
      query: (affiliateCode) => `/products-meta/${affiliateCode}`,
    }),
    getAffiliateProductsIds: builder.query({
      query: (affiliateCode) => `/partner-products/${affiliateCode}`,
    }),
    getProducts: builder.query(
      TK_FLAGS.catalog ? tkQuery(fetchTkProducts) : { query: () => "/products" },
    ),
    getProductBundles: builder.query(
      TK_FLAGS.catalog ? tkQuery(fetchTkBundles) : { query: () => "/get-product-bundles" },
    ),
    trackEvent: builder.mutation({
      query: (eventPayload) => ({
        url: "/track-analytics-events",
        method: "POST",
        body: eventPayload,
      }),
    }),
    addPartnerRequest: builder.mutation({
      query: (formData) => ({
        url: "/partners",
        method: "POST",
        body: formData,
      }),
    }),
    restoreCart: builder.query({
      query: (cartId) => `/get-cart?cart_id=${cartId}`,
    }),
    cancelSubscription: builder.query({
      query: (cancelToken) => `/cancel-subscription/${cancelToken}`,
    }),
  }),
});

export const {
  useGetAffiliateByRefQuery,
  useGetAffiliateProductsMetaQuery,
  useGetAffiliateProductsIdsQuery,
  useVerifyEditableItemQuery,
  useGetProductsQuery,
  useGetProductBundlesQuery,
  useRestoreCartQuery,
  useTrackEventMutation,
  useAddPartnerRequestMutation,
  useCancelSubscriptionQuery
} = apiSlice;